import React, { FC } from 'react';
import classNames from 'classnames';

import Bevel from '@/components/button/Bevel';

import type { RadioButtonProps } from '@/components/radioButton/types';

const RadioButton: FC<RadioButtonProps> = ({
	checked = false,
	children,
	className,
	color = 'sand',
	disabled = false,
	label,
	labelActive,
	name = 'default',
	onChange = () => {},
	onClick,
	partlyChecked = false,
	rounded = false,
	text = '',
	type = 'checkbox',
	value,
	showBevel = true,
	showCheckmark = true,
}) => {
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		e.stopPropagation();
		onChange(e.target.checked, name, value);
	};

	const handleClick = (e: React.MouseEvent<HTMLLabelElement>) => {
		e.stopPropagation();
		onClick?.(e);
	};

	const Label = () => (
		<label
			className={classNames(
				'radio-button',
				`radio-button--${type}`,
				(!showBevel || disabled) && className,
				color,
				{
					'radio-button--checked': checked,
					'radio-button--checked-partly': partlyChecked,
					'radio-button--disabled': disabled,
					'radio-button--rounded': rounded,
				},
			)}
			onClick={handleClick}
		>
			<input
				checked={checked}
				disabled={disabled}
				name={name}
				value={value}
				onChange={handleChange}
				type={type}
			/>
			<span className="radio-button__visual">
				{showCheckmark && !disabled && (
					<span className="radio-button__checkmark">
						{partlyChecked ? (
							<span className="icon icon-minus-dash" />
						) : (
							<span className="icon icon-check" />
						)}
					</span>
				)}
				{children}
				{text && <span className="radio-button__text">{text}</span>}
				{label && (
					<span className={classNames('radio-button__label', { active: labelActive })}>
						{label}
					</span>
				)}
			</span>
		</label>
	);

	if (showBevel) {
		return (
			<Bevel
				color={color}
				className={classNames('radio-button__bevel', showBevel && className)}
				isPressed={checked}
				disabled={disabled}
			>
				<Label />
			</Bevel>
		);
	}

	return <Label />;
};

export default RadioButton;
